import { z } from 'zod';
import { fieldDefinitionSchema } from './fields';

// AUTOMATIONS — trigger → conditions → actions, owned by the site owner
// (Shopify Flow). Owners build them in the Automations panel or by asking the
// Dunea AI; plugins add triggers and actions anyone's automations can use.
//
//   trigger    an event: shop.order.paid, forms.submitted, or a plugin's own
//              ("acme-labels.label.printed", sent through POST /events)
//   condition  checks on the event data: order.total >= 5000
//   action     a built-in (email, webhook, set a metafield, tag an order…) or a
//              plugin action, called as a signed POST like an agent tool
//
// Runs are durable: an event enqueues a run row, a job executes the steps with
// retries, and every run is visible in the panel's run log.

export const CONDITION_OPS = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'not_contains',
  'in',
  'exists',
  'not_exists',
] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];

/** A dotted path into the event data, e.g. "order.total" or "order.items.0.sku". */
const path = z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z0-9_]+){0,6}$/);

export const conditionSchema = z.object({
  path,
  op: z.enum(CONDITION_OPS),
  value: z.unknown().optional(),
});

export type Condition = z.infer<typeof conditionSchema>;

export const conditionsSchema = z
  .object({
    all: z.array(conditionSchema).max(20).default([]),
    any: z.array(conditionSchema).max(20).default([]),
  })
  .default({ all: [], any: [] });

export type Conditions = z.infer<typeof conditionsSchema>;

/** Built-in actions every app has. `{{path}}` in string inputs is filled from the event data. */
export const BUILTIN_ACTIONS = {
  'email.owner': 'Email the site owner',
  'email.customer': 'Email the buyer or lead (order and lead triggers only)',
  'webhook.post': 'POST the event to a URL (signed)',
  'data.set_metafield': 'Set a custom field on the order, product or site',
  'shop.order.add_note': 'Add a note to the order',
  'shop.discount.create': 'Create a single-use discount code',
  'email-marketing.subscribe': 'Add the buyer or lead to your email list',
  'flow.wait': 'Wait before the next step (input: { days?, hours?, minutes? }, up to 30 days)',
} as const;

export type BuiltinAction = keyof typeof BUILTIN_ACTIONS;
export const BUILTIN_ACTION_IDS = Object.keys(BUILTIN_ACTIONS) as BuiltinAction[];

export const actionStepSchema = z.object({
  /** A built-in id, or `<listing-slug>.<action>` for a plugin action. */
  action: z.string().regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9_-]*)+$/),
  input: z.record(z.string(), z.unknown()).default({}),
  /** Keep going when this step fails (default: stop the run). */
  continueOnError: z.boolean().optional(),
});

export type ActionStep = z.infer<typeof actionStepSchema>;

export const automationSchema = z.object({
  name: z.string().min(2).max(80),
  trigger: z.string().regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9_-]*)+$/),
  conditions: conditionsSchema,
  actions: z.array(actionStepSchema).min(1).max(10),
  enabled: z.boolean().default(true),
});

export type Automation = z.infer<typeof automationSchema>;
export type AutomationInput = z.input<typeof automationSchema>;

/** What a plugin manifest may contribute to automations. */
export const automationDeclarationsSchema = z
  .object({
    /** Events your service sends with POST /events/:trigger. Namespaced by your listing slug. */
    triggers: z
      .array(
        z.object({
          id: z.string().regex(/^[a-z][a-z0-9_.-]{1,60}$/),
          title: z.string().min(2).max(60),
          description: z.string().max(300).optional(),
          /** The event data's fields, so owners can build conditions on them. */
          fields: z.array(fieldDefinitionSchema).max(30).default([]),
        }),
      )
      .max(20)
      .default([]),
    /** Actions Dunea calls on your webhookUrl (signed, type automation.action). */
    actions: z
      .array(
        z.object({
          id: z.string().regex(/^[a-z][a-z0-9_-]{1,40}$/),
          title: z.string().min(2).max(60),
          description: z.string().min(10).max(300),
          input: z.array(fieldDefinitionSchema).max(20).default([]),
        }),
      )
      .max(20)
      .default([]),
  })
  .default({ triggers: [], actions: [] });

export type AutomationDeclarations = z.infer<typeof automationDeclarationsSchema>;

/** Body of a plugin automation action call. */
export interface AutomationActionRequest {
  type: 'automation.action';
  action: string;
  installId: string;
  appId: string;
  runId: string;
  input: Record<string, unknown>;
  /** The triggering event's data. */
  event: { type: string; data: Record<string, unknown> };
}

export interface AutomationActionResponse {
  ok: boolean;
  message?: string;
  /** Merged into the run's data for later steps as `steps.<index>.<key>`. */
  output?: Record<string, unknown>;
}

function get(data: unknown, p: string): unknown {
  return p
    .split('.')
    .reduce<unknown>(
      (acc, k) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[k] : undefined),
      data,
    );
}

function test(c: Condition, data: unknown): boolean {
  const v = get(data, c.path);

  switch (c.op) {
    case 'exists':
      return v !== undefined && v !== null;
    case 'not_exists':
      return v === undefined || v === null;
    case 'eq':
      return v === c.value;
    case 'neq':
      return v !== c.value;
    case 'gt':
      return typeof v === 'number' && typeof c.value === 'number' && v > c.value;
    case 'gte':
      return typeof v === 'number' && typeof c.value === 'number' && v >= c.value;
    case 'lt':
      return typeof v === 'number' && typeof c.value === 'number' && v < c.value;
    case 'lte':
      return typeof v === 'number' && typeof c.value === 'number' && v <= c.value;
    case 'contains':
    case 'not_contains': {
      const has = Array.isArray(v)
        ? v.includes(c.value)
        : typeof v === 'string' && typeof c.value === 'string' && v.toLowerCase().includes(c.value.toLowerCase());

      return c.op === 'contains' ? has : !has;
    }
    case 'in':
      return Array.isArray(c.value) && c.value.includes(v);
  }

  // An op from a newer contract this version doesn't know: the condition fails.
  return false;
}

/** Do the conditions pass for this event data? Empty conditions pass. */
export function evaluateConditions(conditions: Conditions, data: unknown): boolean {
  const all = conditions.all.every((c) => test(c, data));
  const any = conditions.any.length === 0 || conditions.any.some((c) => test(c, data));

  return all && any;
}

/** Fill `{{path}}` placeholders from data. Missing values become "". No code, no filters. */
export function renderTemplate(template: string, data: unknown): string {
  return template.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g, (_, p: string) => {
    const v = get(data, p);

    return v === undefined || v === null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  });
}

/** renderTemplate over every string in an action input (deep). */
export function renderInput(input: Record<string, unknown>, data: unknown): Record<string, unknown> {
  const walk = (v: unknown): unknown =>
    typeof v === 'string'
      ? renderTemplate(v, data)
      : Array.isArray(v)
        ? v.map(walk)
        : v && typeof v === 'object'
          ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x)]))
          : v;

  return walk(input) as Record<string, unknown>;
}
